import { Stack } from '@mantine/core'
import { SITE_ENV_LABELS, SiteEnv } from '@pubstack/common/src/site'
import { FunctionComponent } from 'react'
import { Colors } from '~/assets/style/colors'
import { Sizes } from '~/assets/style/tokens'
import { Title_P1 } from '~/components/_styled/titles'
import { Tag } from '~/components/Tag'
import { SITE_PRODUCT_LABEL, SiteProduct } from './siteProduct'

type Props = {
  product: SiteProduct
  medias: { name: string; env: SiteEnv }[]
}

/**
 * The last look before the batch is created.
 *
 * Worth a step of its own for the same reason the seat flow gives one: putting a media on One or Node is
 * one-way, and with a pasted list there is no other moment where the whole batch — every name, and the
 * environment each one ended up on — is on screen at once to be read back.
 */
export const CreateSiteStepReview: FunctionComponent<Props> = ({ product, medias }) => (
  <Stack gap={Sizes[16]} className={'h-full min-h-0'}>
    <Title_P1>Review</Title_P1>
    <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 [&>dd]:m-0 [&>dd]:text-base [&>dd]:leading-[1.625rem] [&>dd]:text-jet [&>dt]:text-sm [&>dt]:leading-[1.375rem] [&>dt]:text-hurricane">
      <dt>Product</dt>
      <dd>{SITE_PRODUCT_LABEL[product]}</dd>
      <dt>Medias</dt>
      <dd>{medias.length}</dd>
    </dl>
    {/* Every media is listed rather than counted: the count is already above, and what a reader checks
        here is that no name was mangled by the paste and that each one is on the environment intended.
        The list scrolls on its own — a pasted batch is exactly the case that overflows the flyout, and a
        review nobody can scroll to the end of reviews nothing. */}
    <Stack gap={Sizes[8]} className={'min-h-0 flex-1 overflow-y-auto pr-4'}>
      {medias.map((media) => (
        <div key={media.name} className={'flex shrink-0 items-center justify-between gap-4'}>
          {/* `min-w-0` alongside `truncate`: a flex item's min-content width is the whole name, so
              without it a long one pushes the environment out of the row instead of ellipsising. */}
          <span className={'min-w-0 truncate text-base leading-[1.625rem] text-jet'}>{media.name}</span>
          <Tag color={Colors.Jet}>{SITE_ENV_LABELS[media.env]}</Tag>
        </div>
      ))}
    </Stack>
  </Stack>
)
